import { useCallback } from 'react';
import {
  useLocation, useNavigate as useRouterNavigate, useSearchParams as useRouterSearchParams,
  resolvePath,
} from 'react-router-dom';
import {
  getDocumentRuntime, getProfileSession, readConfiguration, startTileUrl, tileEntryPath,
} from './runtime';
import { resolveTileProfile, tileForPathname } from './tileRouting';
import { ROUTES } from '../constants/constant';

export const routeStateKey = (runtime, pathname) => JSON.stringify(['vendor-route-v1', runtime.scope, pathname]);
export function restoredRouteState(runtime, pathname, storage = sessionStorage) {
  try {
    const state = JSON.parse(storage.getItem(routeStateKey(runtime, pathname)));
    return state && runtime.ownState(state) ? state : null;
  } catch { return null; }
}

export function useNavigate() {
  const navigate = useRouterNavigate();
  const location = useLocation();
  return useCallback((destination, options = {}) => {
    if (typeof destination === 'number') return navigate(destination);
    const runtime = getDocumentRuntime();
    if (runtime.session) runtime.requireSession();
    const path = resolvePath(destination, location.pathname);
    let tile = null;
    if (![ROUTES.LOGIN, ROUTES.HOME, ROUTES.DASHBOARD].includes(path.pathname)) tile = tileForPathname(path.pathname);
    if (tile !== runtime.tile) {
      if (!tile) {
        window.location.assign(`${path.pathname}?backend=${encodeURIComponent(runtime.profile.id)}`);
        return;
      }
      const configuration = readConfiguration();
      const profile = resolveTileProfile(tile, configuration);
      const session = getProfileSession(profile, window.localStorage);
      const target = new URL(startTileUrl(tile), window.location.origin);
      // A cross-tile record handoff is permitted only inside the EXACT same
      // backend/DB/actor. Different profiles deliberately return to their list.
      if (options.state && session?.namespace === runtime.session?.namespace && profile.id === runtime.profile.id) {
        const key = crypto.randomUUID();
        sessionStorage.setItem(`vendor-handoff:${key}`, JSON.stringify({
          namespace: session.namespace, tile, pathname: path.pathname,
          state: options.state, expires: Date.now() + 60000,
        }));
        target.pathname = path.pathname;
        target.searchParams.set('handoff', key);
      } else if (options.state) {
        target.pathname = tileEntryPath(tile);
        target.searchParams.set('handoff_blocked', '1');
      }
      window.location.assign(target.pathname + target.search);
      return;
    }
    const url = runtime.urlFor(path.pathname + path.search + path.hash);
    const state = runtime.scopedState(options.state);
    if (state) sessionStorage.setItem(routeStateKey(runtime, path.pathname), JSON.stringify(state));
    navigate(url, { ...options, state });
  }, [navigate, location.pathname]);
}

export function useSearchParams() {
  const [params, setParams] = useRouterSearchParams();
  const location = useLocation();
  const update = useCallback((value, options) => {
    const runtime = getDocumentRuntime();
    if (runtime.session) runtime.requireSession();
    const next = new URLSearchParams(typeof value === 'function' ? value(params) : value);
    next.set('backend', runtime.profile.id);
    next.set('workflow', runtime.workflowId);
    setParams(next, { state: location.state, ...options });
  }, [params, setParams, location.state]);
  return [params, update];
}
